// React import
import React, { useState } from 'react';

// Generic imports
import { Mention, MentionsInput } from 'react-mentions';
import _ from 'lodash';

// MUI imports
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { grey, purple } from '@mui/material/colors';

// FontAwesomeIcon imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReplyClock, faPlus, faTrash, faEnvelope, faEllipsis, faMicrochipAi } from '@fortawesome/pro-light-svg-icons';

// Local imports
import Header from './Header.js';


function CampaignMain() {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [audience, setAudience] = useState('');
    const [campaigns, setCampaigns] = useState([{ day: 1, subject: '', body: '' }]);
    const [showDialog, setShowDialog] = useState(null);
    const [dialogDays, setDialogDays] = useState(1);

    const fillIns=[ 
        'Contact Name', 'Company Name', 'Latest One-Off Title', 
        'Latest One-Off Send Date', 'Latest Contract Send Date' ];

    const handleCampaignChange = (index, event, name) => {
        const values = [...campaigns];
        values[index][name] = event.target.value;
        setCampaigns(values);
    };

    const handleAddCampaign = () => {
        setCampaigns([...campaigns, { day: 1, subject: '', body: '' }]);
    };

    const handleRemoveCampaign = (index) => {
        const values = [...campaigns];
        values.splice(index, 1);
        setCampaigns(values);
    };

    return (
        <Box sx={{ paddingBottom: '36px', background: '#fafbfc' }}>
            <Header
                icon={faReplyClock}
                title={title || 'New Campaign'}
                buttons={[]}
            />
            <Box sx={{ padding: '24px' }}>
                <Box sx={{ width: '400px', mb: 2 }}>
                    <TextField
                        fullWidth
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        label={'Title'}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        label={'Description'}
                        sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth>
                        <InputLabel id='audience-label'>Audience</InputLabel>
                        <Select
                            fullWidth
                            value={audience}
                            onChange={(event) => setAudience(event.target.value)}
                            labelId='audience-label'
                            label='Audience'
                        >
                            <MenuItem value='1'>New leads - July 2024</MenuItem>
                            <MenuItem value='2'>Top customers - 2024</MenuItem>
                            <MenuItem value='3'>At risk - July 2024</MenuItem>
                        </Select>
                    </FormControl>
                    {audience === '1' && (<Typography variant='caption' sx={{ fontStyle: 'italic' }}>Audience currently consists of 31 accounts</Typography>)}
                    {audience === '2' && (<Typography variant='caption' sx={{ fontStyle: 'italic' }}>Audience currently consists of 10 accounts</Typography>)}
                    {audience === '3' && (<Typography variant='caption' sx={{ fontStyle: 'italic' }}>Audience currently consists of 6 accounts</Typography>)}
                </Box>

                <Typography variant='h6' sx={{ mb: 2 }}>Steps</Typography>

                {campaigns.map((campaign, index) => (
                    <Accordion sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <FontAwesomeIcon icon={faEnvelope} />
                                    &nbsp;
                                    &nbsp;
                                    <Typography variant='h6' sx={{ flex: 1 }}>Day {_.sumBy(_.range(index), i => campaigns[i].day) + 1}</Typography>
                                </Box>
                                <Box>
                                    <Chip label={(
                                        <Typography variant='caption' sx={{ fontStyle: 'italic' }}>
                                            {index === 0 ? 'Deliver email immediately after the contact is added to the audience' : `Deliver email ${campaign.day} ${campaign.day === 1 ? 'day' : 'days'} after the previous step`}
                                        </Typography>
                                    )} />
                                    <IconButton onClick={e => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setDialogDays(campaign.day);
                                        setShowDialog(index)
                                    }}>
                                        <FontAwesomeIcon icon={faEllipsis} />
                                    </IconButton>
                                </Box>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant='caption' sx={{ pb: 1 }} component={Box}>
                                Subject
                            </Typography>
                            <TextField
                                fullWidth
                                value={campaign.subject}
                                onChange={(event) => handleCampaignChange(index, event, 'subject')}
                                name='subject'
                                sx={{ mb: 2 }}
                            />
                            <Typography variant='caption' sx={{ pb: 1 }} component={Box}>
                                Body
                            </Typography>
                            <Box className='bodytextareawrapper' sx={{ mb: 2, width: '100%' }}>
                                <MentionsInput
                                    value={campaign.body}
                                    onChange={(event) => handleCampaignChange(index, event, 'body')}
                                    style={{ width: '100%', height: '200px' }}
                                    className='bodytextarea'
                                >
                                    <Mention
                                        trigger='{'
                                        data={fillIns.map(f => ({ id: f, display: `{${f}}` }))}
                                    />
                                </MentionsInput>
                            </Box>
                            <Button variant='contained' disabled={campaigns.length < 2} onClick={() => handleRemoveCampaign(index)} color='error' sx={{ mb: 2 }}>
                                <FontAwesomeIcon icon={faTrash} />
                                &nbsp;&nbsp;
                                Remove Step
                            </Button>
                        </AccordionDetails>
                    </Accordion>
                ))}
                <Button variant='outlined' onClick={handleAddCampaign} color='primary' sx={{ mb: 2 }}>
                    <FontAwesomeIcon icon={faPlus} />
                    &nbsp;&nbsp;
                    Add Step
                </Button>
            </Box>
            {!_.isNull(showDialog) && (
                <Dialog open={true} fullWidth maxWidth='md' onClose={() => setShowDialog(null)}>
                    <DialogTitle>Configure Step</DialogTitle>
                    <DialogContent>
                        <Typography variant='body2' sx={{ fontWeight: 800 }} component={Box}>
                            Action to perform
                        </Typography>
                        <Box sx={{ padding: '12px 24px' }}>
                            <Box sx={{ background: grey[100], border: `1px solid ${grey[300]}`, padding: '12px 12px', display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ background: purple[100], width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}>
                                    <FontAwesomeIcon style={{ fontSize: '24px', color: purple[500] }} icon={faEnvelope} />
                                </Box>
                                <Box sx={{ ml: 2, display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant='body1' sx={{ fontWeight: 800, mr: 2 }} component={Box}>
                                            Send an email
                                        </Typography>
                                        <FontAwesomeIcon icon={faMicrochipAi} />
                                    </Box>
                                    <Typography variant='caption'>
                                        Emails are delivered automatically
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1 }} />
                                <Button variant='text' sx={{ textTransform: 'none' }}>
                                    Change action
                                </Button>
                            </Box>
                        </Box>
                        <Typography variant='body2' sx={{ fontWeight: 800, mt: 3 }} component={Box}>
                            When to start this step
                        </Typography>
                        <Typography variant='caption' sx={{ pt: 1, pb: 1 }} component={Box}>
                            Set the amount of time you'd like to wait between steps.
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, width: '100%' }}>
                            <TextField
                                fullWidth
                                value={dialogDays}
                                onChange={(event) => setDialogDays(event.target.value)}
                                label='Days after the previous step'
                            />
                        </Box>
                        <Button variant='text' sx={{ textTransform: 'none', mt: 2 }}>
                            Show advanced options for triggering this step...
                        </Button>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            handleCampaignChange(showDialog, {target: { value: parseInt(dialogDays) } }, 'day')
                            setShowDialog(null)
                        }}
                            color='primary'>
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

        </Box>
    );
}

export default CampaignMain;
